import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { UserCircle, Plus, FileText, TrendingDown, TrendingUp, Cloud } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { uploadPdfToDrive } from "@/lib/drive";

const STATUS = ["active", "suspended", "blacklisted"];
const STATUS_COLOR = { active: "text-primary bg-primary/10", suspended: "text-amber-600 bg-amber-500/10", blacklisted: "text-destructive bg-destructive/10" };

export default function CustomersPage() {
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [customers, setCustomers] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [stmt, setStmt] = useState(null);
    const [q, setQ] = useState("");

    const load = async () => {
        const [f, c] = await Promise.all([
            api.get("/farm").then(r => r.data),
            api.get("/customers/with-balance").then(r => r.data),
        ]);
        setFarm(f);
        setCustomers(c);
    };
    useEffect(() => { load(); }, []);
    const cur = (v) => formatCurrency(v, farm.currency);

    const save = async () => {
        const body = { ...dlg, credit_limit: Number(dlg.credit_limit) || 0 };
        if (dlg.id) await api.put(`/customers/${dlg.id}`, body);
        else await api.post("/customers", body);
        toast.success("Customer saved");
        setDlg(null); load();
    };

    const del = async (c) => {
        if (!window.confirm(`Delete ${c.name}?`)) return;
        await api.delete(`/customers/${c.id}`);
        toast.success("Deleted"); load();
    };

    const openStatement = async (c) => {
        const r = await api.get(`/customers/${c.id}/statement`);
        setStmt(r.data);
    };

    const buildStatementDoc = () => {
        if (!stmt) return null;
        const doc = new jsPDF();
        doc.setFontSize(14); doc.setFont(undefined, "bold");
        doc.text(`Statement of Account`, 14, 15);
        doc.setFontSize(11); doc.setFont(undefined, "normal");
        doc.text(`${stmt.customer.name}${stmt.customer.business_name ? " — " + stmt.customer.business_name : ""}`, 14, 22);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
        doc.text(`Total Billed: ${cur(stmt.total_billed)}`, 14, 38);
        doc.text(`Total Paid: ${cur(stmt.total_paid)}`, 14, 44);
        doc.setFont(undefined, "bold");
        doc.text(`Outstanding: ${cur(stmt.outstanding)}`, 14, 50);
        autoTable(doc, {
            startY: 58,
            head: [["Date", "Type", "Ref", "Debit", "Credit", "Balance"]],
            body: stmt.entries.map(e => [e.date, e.type, e.ref, e.debit ? cur(e.debit) : "", e.credit ? cur(e.credit) : "", cur(e.balance)]),
            styles: { fontSize: 9 },
        });
        return doc;
    };
    const printStatement = () => {
        const doc = buildStatementDoc();
        if (doc) doc.save(`${stmt.customer.name}-statement.pdf`);
    };
    const sendStatementToDrive = async () => {
        const doc = buildStatementDoc();
        if (!doc) return;
        try { await uploadPdfToDrive(doc, `${stmt.customer.name}-statement.pdf`, "Receipts"); }
        catch (e) { console.error("Drive statement upload failed:", e); /* toast shown by helper */ }
    };

    const filtered = useMemo(() => {
        const ql = q.toLowerCase();
        return customers.filter(c => !ql || (c.name || "").toLowerCase().includes(ql) || (c.phone || "").includes(q));
    }, [customers, q]);

    // KPIs
    const { totalOutstanding, totalBilled, overLimit } = useMemo(() => ({
        totalOutstanding: customers.reduce((s, c) => s + Number(c.outstanding || 0), 0),
        totalBilled: customers.reduce((s, c) => s + Number(c.total_billed || 0), 0),
        overLimit: customers.filter(c => Number(c.credit_limit || 0) > 0 && Number(c.outstanding || 0) > Number(c.credit_limit || 0)).length,
    }), [customers]);

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="tiny-label">{i18n.t("batch3.b0dd615cbbdc")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.035eae60290f")}</h1>
                    <p className="text-muted-foreground mt-1">{i18n.t("batch3.bfe0a80d77d5")}</p>
                </div>
                <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setDlg({ name: "", business_name: "", phone: "", email: "", address: "", credit_limit: 0, account_status: "active", notes: "" })} className="h-11" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" />{i18n.t("batch3.c92c0c39c1e8")}</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch3.0e85749a6f40")}</DialogTitle></DialogHeader>
                        {dlg && <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="tiny-label">{i18n.t("batch3.d145bb830936")}</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} className="h-11 mt-1" data-testid="cust-name-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.4fee51fde270")}</Label><Input value={dlg.business_name || ""} onChange={e => setDlg({ ...dlg, business_name: e.target.value })} className="h-11 mt-1" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.77064d526523")}</Label><Input value={dlg.phone || ""} onChange={e => setDlg({ ...dlg, phone: e.target.value })} className="h-11 mt-1" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.84add5b29527")}</Label><Input value={dlg.email || ""} onChange={e => setDlg({ ...dlg, email: e.target.value })} className="h-11 mt-1" /></div>
                                <div className="col-span-2"><Label className="tiny-label">{i18n.t("batch3.d70f93df5e8f")}</Label><Input value={dlg.address || ""} onChange={e => setDlg({ ...dlg, address: e.target.value })} className="h-11 mt-1" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.c2f9c4fd53fb")}{farm.currency})</Label><Input type="number" value={dlg.credit_limit} onChange={e => setDlg({ ...dlg, credit_limit: e.target.value })} className="h-11 mt-1 font-mono" data-testid="cust-credit-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.bae7d5be7082")}</Label>
                                    <Select value={dlg.account_status || "active"} onValueChange={v => setDlg({ ...dlg, account_status: v })}>
                                        <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2"><Label className="tiny-label">{i18n.t("batch3.70440046a3dc")}</Label><Textarea value={dlg.notes || ""} onChange={e => setDlg({ ...dlg, notes: e.target.value })} rows={2} /></div>
                            </div>
                            <Button onClick={save} className="h-11 w-full" disabled={!dlg.name} data-testid="save-customer-btn">{i18n.t("batch3.efc007a393f6")}</Button>
                        </div>}
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4"><div className="tiny-label">{i18n.t("batch3.21bc3ec9ecc6")}</div><div className="kpi-value text-2xl font-bold mt-2">{customers.length}</div></Card>
                <Card className="p-4"><div className="tiny-label">{i18n.t("batch3.d2faabf68a9f")}</div><div className="kpi-value text-2xl font-bold mt-2">{cur(totalBilled)}</div></Card>
                <Card className="p-4 bg-amber-500/5"><div className="tiny-label text-amber-700">{i18n.t("batch3.f8ee57ec8645")}</div><div className="kpi-value text-2xl font-bold mt-2 text-amber-600">{cur(totalOutstanding)}</div></Card>
                <Card className={`p-4 ${overLimit > 0 ? "bg-destructive/5" : ""}`}><div className={`tiny-label ${overLimit > 0 ? "text-destructive" : ""}`}>{i18n.t("batch3.b8bcab1c909b")}</div><div className={`kpi-value text-2xl font-bold mt-2 ${overLimit > 0 ? "text-destructive" : ""}`}>{overLimit}</div></Card>
            </div>

            <Card className="p-4">
                <div className="flex justify-between mb-3">
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder={i18n.t("batch3.8159bd4e7e98")} className="h-10 max-w-xs" data-testid="cust-search-input" />
                </div>
                <Table>
                    <TableHeader><TableRow>
                        <TableHead>{i18n.t("batch3.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch3.77064d526523")}</TableHead><TableHead>{i18n.t("batch3.bae7d5be7082")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch3.c2f1302a0a50")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch3.f8ee57ec8645")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch3.7c62a1424469")}</TableHead>
                        <TableHead className="text-right">{i18n.t("batch3.35f8f37b92ae")}</TableHead>
                        <TableHead />
                    </TableRow></TableHeader>
                    <TableBody>
                        {filtered.map(c => {
                            const overLim = Number(c.credit_limit || 0) > 0 && Number(c.outstanding || 0) > Number(c.credit_limit || 0);
                            return <TableRow key={c.id} className={overLim ? "bg-destructive/5" : ""}>
                                <TableCell>
                                    <div className="font-semibold">{c.name}</div>
                                    {c.business_name && <div className="text-xs text-muted-foreground">{c.business_name}</div>}
                                </TableCell>
                                <TableCell className="font-mono text-xs">{c.phone || "—"}</TableCell>
                                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[c.account_status || "active"]}`}>{c.account_status || "active"}</span></TableCell>
                                <TableCell className="text-right font-mono">{Number(c.credit_limit) > 0 ? cur(c.credit_limit) : "—"}</TableCell>
                                <TableCell className={`text-right font-mono font-semibold ${Number(c.outstanding) > 0 ? "text-amber-600" : ""}`}>{cur(c.outstanding)}</TableCell>
                                <TableCell className="text-right font-mono">{Number(c.credit_limit) > 0 ? cur(c.credit_available) : "—"}</TableCell>
                                <TableCell className="text-right font-mono">{c.invoice_count || 0}</TableCell>
                                <TableCell className="text-right space-x-1">
                                    <Button size="sm" variant="outline" onClick={() => openStatement(c)} data-testid={`stmt-btn-${c.id}`}><FileText className="w-3 h-3 mr-1" />{i18n.t("batch3.a72ca256dc49")}</Button>
                                    <Button size="sm" variant="outline" onClick={() => setDlg(c)}>{i18n.t("batch3.5301648dcf6b")}</Button>
                                    <Button size="sm" variant="destructive" onClick={() => del(c)}>×</Button>
                                </TableCell>
                            </TableRow>;
                        })}
                        {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground"><UserCircle className="w-12 h-12 mx-auto mb-2 opacity-40" />{i18n.t("batch3.93b2cc26caff")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>

            {/* Statement dialog */}
            <Dialog open={!!stmt} onOpenChange={o => !o && setStmt(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="statement-dialog">
                    <DialogHeader><DialogTitle>{i18n.t("batch3.c201cc5e25b6")} {stmt?.customer?.name}</DialogTitle></DialogHeader>
                    {stmt && <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Card className="p-3"><div className="tiny-label">{i18n.t("batch3.d2faabf68a9f")}</div><div className="font-bold font-mono mt-1">{cur(stmt.total_billed)}</div></Card>
                            <Card className="p-3 bg-primary/5"><div className="tiny-label">{i18n.t("batch3.6a151d73d61b")}</div><div className="font-bold font-mono mt-1 text-primary">{cur(stmt.total_paid)}</div></Card>
                            <Card className={`p-3 ${stmt.outstanding > 0 ? "bg-amber-500/5" : ""}`}><div className="tiny-label">{i18n.t("batch3.f8ee57ec8645")}</div><div className={`font-bold font-mono mt-1 ${stmt.outstanding > 0 ? "text-amber-600" : ""}`}>{cur(stmt.outstanding)}</div></Card>
                            <Card className="p-3"><div className="tiny-label">{i18n.t("batch3.7d87e329b6f5")}</div><div className="font-bold font-mono mt-1">{stmt.credit_limit > 0 ? cur(stmt.credit_available) : "—"}</div></Card>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>{i18n.t("batch3.eb9a4bc1c0c1")}</TableHead><TableHead>{i18n.t("batch3.3deb74565196")}</TableHead><TableHead>{i18n.t("batch3.c89dd33c646c")}</TableHead><TableHead className="text-right">{i18n.t("batch3.cf558941e04e")}</TableHead><TableHead className="text-right">{i18n.t("batch3.8a45d339a638")}</TableHead><TableHead className="text-right">{i18n.t("batch3.90eef613042c")}</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {stmt.entries.map((e) => (
                                    <TableRow key={e.id || `${e.type}-${e.ref}-${e.date}`}>
                                        <TableCell className="font-mono text-xs">{e.date}</TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center gap-1 text-xs ${e.type === "invoice" ? "text-amber-600" : "text-primary"}`}>
                                                {e.type === "invoice" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                {e.type}
                                            </span>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">{e.ref}</TableCell>
                                        <TableCell className="text-right font-mono">{e.debit ? cur(e.debit) : ""}</TableCell>
                                        <TableCell className="text-right font-mono text-primary">{e.credit ? cur(e.credit) : ""}</TableCell>
                                        <TableCell className="text-right font-mono font-semibold">{cur(e.balance)}</TableCell>
                                    </TableRow>
                                ))}
                                {stmt.entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">{i18n.t("batch3.f794b6c6d153")}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                        <div className="grid grid-cols-2 gap-2">
                            <Button onClick={printStatement} className="h-11" data-testid="print-statement-btn"><FileText className="w-4 h-4 mr-2" />{i18n.t("batch3.98e5ef062b2a")}</Button>
                            <Button onClick={sendStatementToDrive} variant="outline" className="h-11" data-testid="drive-statement-btn"><Cloud className="w-4 h-4 mr-2" />{i18n.t("batch3.3bcdd674f34e")}</Button>
                        </div>
                    </div>}
                </DialogContent>
            </Dialog>
        </div>
    );
}
